import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Button } from '../../common/Button';
import { MultiSelectChips } from '../AddPatientForm/components/MultiSelectChips';
import { DateInput } from '../../common/DateInput';
import { Input as BaseInput, type InputProps } from '../../common/Input';
import { Textarea as BaseTextarea } from '../../common/Textarea';
import InputSelect from '../../../shared/components/input-select';
import {
  ALBUMINURIA_OPTIONS,
  BREAST_EXAM_OPTIONS,
  CERVICAL_STATE_OPTIONS,
  CONJUNCTIVA_PALMS_OPTIONS,
  DIPSTICK_OPTIONS,
  DRUG_CLASS_OPTIONS,
  EDEMA_OPTIONS,
  HBS_OPTIONS,
  HIV_OPTIONS,
  OTHER_LAB_RESULT_OPTIONS,
  PELVIC_ASSESSMENT_OPTIONS,
  RAI_OPTIONS,
  RUBEOLA_OPTIONS,
  SKELETAL_ABNORMALITIES_OPTIONS,
  SPECULUM_EXAM_OPTIONS,
  SYPHILIS_OPTIONS,
  TOXOPLASMOSIS_OPTIONS,
  TREATMENT_OPTIONS,
  YES_NO_OPTIONS,
} from './constants';
import { toCalendarInputDate } from './date-utils';

export interface ClinicalPrenatalValues {
  study_date?: string | null;
  maternal_weight_kg?: number | null;
  blood_pressure?: string | null;
  mother_heart_rate?: number | null;
  skeletal_abnormalities?: string | null;
  skeletal_abnormalities_note?: string | null;
  conjunctiva_palms_condition?: string | null;
  breast_exam?: string | null;
  breast_exam_note?: string | null;
  edema?: string | null;
  uterine_height_cm?: number | null;
  fetal_movement_present?: boolean | null;
  speculum_exam?: string | null;
  speculum_exam_note?: string | null;
  cervical_state?: string | null;
  presentation?: 'cephalic' | 'breech' | 'transverse' | null;
  pelvic_assessment?: string | null;
  urine_nitrites?: string | null;
  urine_leucocytes?: string | null;
  urine_albuminuria?: string | null;
  urine_blood?: string | null;
  urine_ph?: string | null;
  rubeola?: string | null;
  toxoplasmosis?: string | null;
  syphilis_tpha_vdrl?: string | null;
  hbs_ag?: string | null;
  hiv_serology?: string | null;
  hemoglobin_g_dl?: number | null;
  hematocrit_pct?: number | null;
  platelets_giga_l?: number | null;
  glycemia_gaj_g_l?: number | null;
  glycemia_hgpo_a1h_g_l?: number | null;
  glycemia_hgpo_a2h_g_l?: number | null;
  rai_if_rh_negative?: string | null;
  rai_if_rh_negative_note?: string | null;
  other_lab_results?: string | null;
  other_lab_results_note?: string | null;
  supp_iron?: boolean | null;
  supp_iron_note?: string | null;
  supp_vitamin_d?: boolean | null;
  supp_vitamin_d_note?: string | null;
  supp_folic_acid?: boolean | null;
  supp_folic_acid_note?: string | null;
  treatment?: string | null;
  treatment_note?: string | null;
  drug_classes?: string | null;
  drug_classes_note?: string | null;
  notes?: string | null;
}

export interface ClinicalPrenatalMeta {
  trimesterLabel?: string | null;
  gestationalAgeLabel?: string | null;
  edd?: string | null;
  studyDate?: string | null;
}

interface FormState {
  study_date: string;
  blood_pressure: string;
  mother_heart_rate: string;
  skeletal_abnormalities: string;
  skeletal_abnormalities_note: string;
  conjunctiva_palms_condition: string;
  breast_exam: string;
  breast_exam_note: string;
  edema: string;
  uterine_height_cm: string;
  speculum_exam: string;
  speculum_exam_note: string;
  cervical_state: string;
  pelvic_assessment: string;
  urine_nitrites: string;
  urine_leucocytes: string;
  urine_albuminuria: string;
  urine_blood: string;
  urine_ph: string;
  rubeola: string;
  toxoplasmosis: string;
  syphilis_tpha_vdrl: string;
  hbs_ag: string;
  hiv_serology: string;
  hemoglobin_g_dl: string;
  hematocrit_pct: string;
  platelets_giga_l: string;
  glycemia_gaj_g_l: string;
  glycemia_hgpo_a1h_g_l: string;
  glycemia_hgpo_a2h_g_l: string;
  rai_if_rh_negative: string;
  rai_if_rh_negative_note: string;
  other_lab_results: string;
  other_lab_results_note: string;
  supp_iron: 'yes' | 'no' | '';
  supp_iron_note: string;
  supp_vitamin_d: 'yes' | 'no' | '';
  supp_vitamin_d_note: string;
  supp_folic_acid: 'yes' | 'no' | '';
  supp_folic_acid_note: string;
  treatment: string;
  treatment_note: string;
  drug_classes: string;
  drug_classes_note: string;
  notes: string;
}

const EMPTY_STATE: FormState = {
  study_date: '',
  blood_pressure: '',
  mother_heart_rate: '',
  skeletal_abnormalities: '',
  skeletal_abnormalities_note: '',
  conjunctiva_palms_condition: '',
  breast_exam: '',
  breast_exam_note: '',
  edema: '',
  uterine_height_cm: '',
  speculum_exam: '',
  speculum_exam_note: '',
  cervical_state: '',
  pelvic_assessment: '',
  urine_nitrites: '',
  urine_leucocytes: '',
  urine_albuminuria: '',
  urine_blood: '',
  urine_ph: '',
  rubeola: '',
  toxoplasmosis: '',
  syphilis_tpha_vdrl: '',
  hbs_ag: '',
  hiv_serology: '',
  hemoglobin_g_dl: '',
  hematocrit_pct: '',
  platelets_giga_l: '',
  glycemia_gaj_g_l: '',
  glycemia_hgpo_a1h_g_l: '',
  glycemia_hgpo_a2h_g_l: '',
  rai_if_rh_negative: '',
  rai_if_rh_negative_note: '',
  other_lab_results: '',
  other_lab_results_note: '',
  supp_iron: '',
  supp_iron_note: '',
  supp_vitamin_d: '',
  supp_vitamin_d_note: '',
  supp_folic_acid: '',
  supp_folic_acid_note: '',
  treatment: '',
  treatment_note: '',
  drug_classes: '',
  drug_classes_note: '',
  notes: '',
};


const COMPACT_INPUT_CLASS =
  'h-7 rounded-md border-slate-300 px-2 text-xs dark:border-gray-600 dark:bg-gray-800';
const COMPACT_TEXTAREA_CLASS =
  'min-h-[58px] rounded-md border-slate-300 px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-800';

function Input({ className, ...props }: InputProps) {
  return (
    <BaseInput
      className={`${COMPACT_INPUT_CLASS}${className ? ` ${className}` : ''}`}
      {...props}
    />
  );
}

function Textarea({
  className,
  ...props
}: React.ComponentProps<'textarea'> & { className?: string }) {
  return (
    <BaseTextarea
      className={`${COMPACT_TEXTAREA_CLASS}${className ? ` ${className}` : ''}`}
      {...props}
    />
  );
}

function hydrate(
  values?: Partial<ClinicalPrenatalValues> | null,
  fallbackStudyDate?: string | null,
  mode: 'create' | 'update' = 'create'
): FormState {
  if (!values) {
    return {
      ...EMPTY_STATE,
      study_date: toCalendarInputDate(
        fallbackStudyDate ??
          (mode === 'create' ? dayjs().format('YYYY-MM-DD') : null)
      ),
    };
  }

  const str = (value: unknown): string =>
    value === null || value === undefined ? '' : String(value);
  const customValue = (
    value: unknown,
    note: unknown,
    otherValue: string
  ): string => {
    const rawValue = str(value).trim();
    const rawNote = str(note).trim();

    if (rawValue === otherValue && rawNote) {
      return rawNote;
    }

    return str(value);
  };
  const shouldClearLegacyOtherNote = (
    value: unknown,
    note: unknown,
    otherValue: string
  ): boolean => str(value).trim() === otherValue && str(note).trim() !== '';
  const bool = (value: unknown): 'yes' | 'no' | '' => {
    if (value === true) return 'yes';
    if (value === false) return 'no';
    return '';
  };

  const skeletalAbnormalities = customValue(
    values.skeletal_abnormalities,
    values.skeletal_abnormalities_note,
    'other_anomaly'
  );
  const breastExam = customValue(
    values.breast_exam,
    values.breast_exam_note,
    'other_anomaly'
  );
  const speculumExam = customValue(
    values.speculum_exam,
    values.speculum_exam_note,
    'other_anomaly'
  );
  const raiIfRhNegative = customValue(
    values.rai_if_rh_negative,
    values.rai_if_rh_negative_note,
    'other_antibody'
  );

  return {
    study_date: toCalendarInputDate(
      values.study_date ??
        fallbackStudyDate ??
        (mode === 'create' ? dayjs().format('YYYY-MM-DD') : null)
    ),
    blood_pressure: str(values.blood_pressure),
    mother_heart_rate: str(values.mother_heart_rate),
    skeletal_abnormalities: skeletalAbnormalities,
    skeletal_abnormalities_note: shouldClearLegacyOtherNote(
      values.skeletal_abnormalities,
      values.skeletal_abnormalities_note,
      'other_anomaly'
    )
      ? ''
      : str(values.skeletal_abnormalities_note),
    conjunctiva_palms_condition: str(values.conjunctiva_palms_condition),
    breast_exam: breastExam,
    breast_exam_note: shouldClearLegacyOtherNote(
      values.breast_exam,
      values.breast_exam_note,
      'other_anomaly'
    )
      ? ''
      : str(values.breast_exam_note),
    edema: str(values.edema),
    uterine_height_cm: str(values.uterine_height_cm),
    speculum_exam: speculumExam,
    speculum_exam_note: shouldClearLegacyOtherNote(
      values.speculum_exam,
      values.speculum_exam_note,
      'other_anomaly'
    )
      ? ''
      : str(values.speculum_exam_note),
    cervical_state: str(values.cervical_state),
    pelvic_assessment: str(values.pelvic_assessment),
    urine_nitrites: str(values.urine_nitrites),
    urine_leucocytes: str(values.urine_leucocytes),
    urine_albuminuria: str(values.urine_albuminuria),
    urine_blood: str(values.urine_blood),
    urine_ph: str(values.urine_ph),
    rubeola: str(values.rubeola),
    toxoplasmosis: str(values.toxoplasmosis),
    syphilis_tpha_vdrl: str(values.syphilis_tpha_vdrl),
    hbs_ag: str(values.hbs_ag),
    hiv_serology: str(values.hiv_serology),
    hemoglobin_g_dl: str(values.hemoglobin_g_dl),
    hematocrit_pct: str(values.hematocrit_pct),
    platelets_giga_l: str(values.platelets_giga_l),
    glycemia_gaj_g_l: str(values.glycemia_gaj_g_l),
    glycemia_hgpo_a1h_g_l: str(values.glycemia_hgpo_a1h_g_l),
    glycemia_hgpo_a2h_g_l: str(values.glycemia_hgpo_a2h_g_l),
    rai_if_rh_negative: raiIfRhNegative,
    rai_if_rh_negative_note: shouldClearLegacyOtherNote(
      values.rai_if_rh_negative,
      values.rai_if_rh_negative_note,
      'other_antibody'
    )
      ? ''
      : str(values.rai_if_rh_negative_note),
    other_lab_results: str(values.other_lab_results),
    other_lab_results_note: str(values.other_lab_results_note),
    supp_iron: bool(values.supp_iron),
    supp_iron_note: str(values.supp_iron_note),
    supp_vitamin_d: bool(values.supp_vitamin_d),
    supp_vitamin_d_note: str(values.supp_vitamin_d_note),
    supp_folic_acid: bool(values.supp_folic_acid),
    supp_folic_acid_note: str(values.supp_folic_acid_note),
    treatment: str(values.treatment),
    treatment_note: str(values.treatment_note),
    drug_classes: str(values.drug_classes),
    drug_classes_note: str(values.drug_classes_note),
    notes: str(values.notes),
  };
}

function normalizeString(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

function normalizeNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeBoolean(value: FormState['supp_iron']): boolean | null {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
}

function serialize(state: FormState): ClinicalPrenatalValues {
  return {
    study_date: normalizeString(state.study_date),
    blood_pressure: normalizeString(state.blood_pressure),
    mother_heart_rate: normalizeNumber(state.mother_heart_rate),
    skeletal_abnormalities: normalizeString(state.skeletal_abnormalities),
    skeletal_abnormalities_note: normalizeString(
      state.skeletal_abnormalities_note
    ),
    conjunctiva_palms_condition: normalizeString(
      state.conjunctiva_palms_condition
    ),
    breast_exam: normalizeString(state.breast_exam),
    breast_exam_note: normalizeString(state.breast_exam_note),
    edema: normalizeString(state.edema),
    uterine_height_cm: normalizeNumber(state.uterine_height_cm),
    speculum_exam: normalizeString(state.speculum_exam),
    speculum_exam_note: normalizeString(state.speculum_exam_note),
    cervical_state: normalizeString(state.cervical_state),
    pelvic_assessment: normalizeString(state.pelvic_assessment),
    urine_nitrites: normalizeString(state.urine_nitrites),
    urine_leucocytes: normalizeString(state.urine_leucocytes),
    urine_albuminuria: normalizeString(state.urine_albuminuria),
    urine_blood: normalizeString(state.urine_blood),
    urine_ph: normalizeString(state.urine_ph),
    rubeola: normalizeString(state.rubeola),
    toxoplasmosis: normalizeString(state.toxoplasmosis),
    syphilis_tpha_vdrl: normalizeString(state.syphilis_tpha_vdrl),
    hbs_ag: normalizeString(state.hbs_ag),
    hiv_serology: normalizeString(state.hiv_serology),
    hemoglobin_g_dl: normalizeNumber(state.hemoglobin_g_dl),
    hematocrit_pct: normalizeNumber(state.hematocrit_pct),
    platelets_giga_l: normalizeNumber(state.platelets_giga_l),
    glycemia_gaj_g_l: normalizeNumber(state.glycemia_gaj_g_l),
    glycemia_hgpo_a1h_g_l: normalizeNumber(state.glycemia_hgpo_a1h_g_l),
    glycemia_hgpo_a2h_g_l: normalizeNumber(state.glycemia_hgpo_a2h_g_l),
    rai_if_rh_negative: normalizeString(state.rai_if_rh_negative),
    rai_if_rh_negative_note: normalizeString(state.rai_if_rh_negative_note),
    other_lab_results: normalizeString(state.other_lab_results),
    other_lab_results_note: normalizeString(state.other_lab_results_note),
    supp_iron: normalizeBoolean(state.supp_iron),
    supp_iron_note: normalizeString(state.supp_iron_note),
    supp_vitamin_d: normalizeBoolean(state.supp_vitamin_d),
    supp_vitamin_d_note: normalizeString(state.supp_vitamin_d_note),
    supp_folic_acid: normalizeBoolean(state.supp_folic_acid),
    supp_folic_acid_note: normalizeString(state.supp_folic_acid_note),
    treatment: normalizeString(state.treatment),
    treatment_note: normalizeString(state.treatment_note),
    drug_classes: normalizeString(state.drug_classes),
    drug_classes_note: normalizeString(state.drug_classes_note),
    notes: normalizeString(state.notes),
  };
}

const EMPTY_SELECT_VALUE = '__unspecified__';

function SelectField({
  value,
  onChange,
  options,
  t,
  ariaLabel,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  t: (key: string) => string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  return (
    <InputSelect
      value={value === '' ? EMPTY_SELECT_VALUE : value}
      onChange={(next: string) =>
        onChange(next === EMPTY_SELECT_VALUE ? '' : next)
      }
      options={options.map((option) => ({
        value: option.value === '' ? EMPTY_SELECT_VALUE : option.value,
        label: t(option.label),
      }))}
      aria-label={ariaLabel}
      isDisabled={disabled}
      isClearable={false}
      isSearchable={false}
      styles={COMPACT_SELECT_STYLES}
    />
  );
}

function withCurrentValueOption(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
  t: (key: string, params?: Record<string, unknown>) => string
) {
  const normalized = value.trim();
  if (!normalized || options.some((option) => option.value === normalized)) {
    return options;
  }

  return [
    {
      value: normalized,
      label: t('prenatalExam.options.savedValue', { value: normalized }),
    },
    ...options,
  ];
}

function decodeMultiSelect(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function encodeMultiSelect(values: string[]): string {
  return Array.from(
    new Set(values.map((item) => item.trim()).filter(Boolean))
  ).join(', ');
}

function hasText(value: string): boolean {
  return value.trim() !== '';
}

const CUSTOM_SELECT_CONFIG = {
  edema: { options: EDEMA_OPTIONS, customOptionValue: 'other' },
  conjunctiva_palms_condition: {
    options: CONJUNCTIVA_PALMS_OPTIONS,
    customOptionValue: 'other',
  },
  cervical_state: {
    options: CERVICAL_STATE_OPTIONS,
    customOptionValue: 'other',
  },
  pelvic_assessment: {
    options: PELVIC_ASSESSMENT_OPTIONS,
    customOptionValue: 'other',
  },
  skeletal_abnormalities: {
    options: SKELETAL_ABNORMALITIES_OPTIONS,
    customOptionValue: 'other_anomaly',
  },
  breast_exam: {
    options: BREAST_EXAM_OPTIONS,
    customOptionValue: 'other_anomaly',
  },
  speculum_exam: {
    options: SPECULUM_EXAM_OPTIONS,
    customOptionValue: 'other_anomaly',
  },
  rai_if_rh_negative: {
    options: RAI_OPTIONS,
    customOptionValue: 'other_antibody',
  },
} as const;

type CustomSelectField = keyof typeof CUSTOM_SELECT_CONFIG;

function isCustomSelectValue(
  key: CustomSelectField,
  value: string | null | undefined
): boolean {
  const normalized = String(value ?? '').trim();
  const { options, customOptionValue } = CUSTOM_SELECT_CONFIG[key];
  return (
    normalized === customOptionValue ||
    (normalized !== '' && !options.some((option) => option.value === normalized))
  );
}

function toTranslatedSelectOptions(
  options: ReadonlyArray<{ value: string; label: string }>,
  t: (key: string, params?: Record<string, unknown>) => string,
  { includeEmpty = true }: { includeEmpty?: boolean } = {}
): Array<{ value: string; label: string }> {
  return options
    .filter((option) => includeEmpty || option.value !== '')
    .map((option) => ({
      value: option.value,
      label: t(option.label),
    }));
}

const COMPACT_SELECT_STYLES = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 28,
    borderRadius: 6,
    borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    backgroundColor: state.isDisabled ? '#f8fafc' : '#ffffff',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1',
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    minHeight: 28,
    padding: '0 8px',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: 12,
    color: '#0f172a',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: 12,
    color: '#0f172a',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: 12,
    color: '#94a3b8',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    minHeight: 28,
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: 4,
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    padding: 4,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 30,
    fontSize: 12,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: 12,
    backgroundColor: state.isFocused ? '#eff6ff' : '#ffffff',
    color: '#0f172a',
  }),
};

function CreatableSelectField({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <InputSelect
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      isCreatable
      isSearchable
      isClearable
      isDisabled={disabled}
      styles={COMPACT_SELECT_STYLES}
    />
  );
}

function Field({
  label,
  children,
  hint,
  className,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <label className="text-[11px] font-medium text-slate-500 dark:text-gray-400">
        {label}
      </label>
      {children}
      {hint ? (
        <span className="text-[10px] text-slate-400 dark:text-gray-500">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-3 border-b border-primary-dui/15 pb-1.5">
      <p className="text-xs font-bold uppercase tracking-wide text-primary-dui">
        {title}
      </p>
      {subtitle ? (
        <p className="mt-1 text-[11px] text-slate-500 dark:text-gray-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900/40">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

function SectionCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-slate-200/80 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/30 ${
        className ?? ''
      }`}
    >
      {children}
    </section>
  );
}

function formatMetaDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
}


function useClinicalPrenatalSummaryCards(
  pregnancyMeta: ClinicalPrenatalMeta | undefined
) {
  const { t } = useTranslation('Patient');

  return useMemo(
    () =>
      [
        {
          label: t('prenatalExam.summary.trimester'),
          value: pregnancyMeta?.trimesterLabel || null,
        },
        {
          label: t('prenatalExam.summary.gestationalAge'),
          value: pregnancyMeta?.gestationalAgeLabel || null,
        },
        {
          label: t('prenatalExam.summary.edd'),
          value: formatMetaDate(pregnancyMeta?.edd),
        },
        {
          label: t('prenatalExam.summary.studyDate'),
          value: formatMetaDate(pregnancyMeta?.studyDate),
        },
      ].filter((item): item is { label: string; value: string } =>
        Boolean(item.value)
      ),
    [
      pregnancyMeta?.trimesterLabel,
      pregnancyMeta?.gestationalAgeLabel,
      pregnancyMeta?.edd,
      pregnancyMeta?.studyDate,
      t,
    ]
  );
}

export interface ClinicalPrenatalIntroCardProps {
  pregnancyMeta?: ClinicalPrenatalMeta;
  helperText?: string;
}

export function ClinicalPrenatalIntroCard({
  pregnancyMeta,
  helperText,
}: ClinicalPrenatalIntroCardProps) {
  const { t } = useTranslation('Patient');
  const summaryCards = useClinicalPrenatalSummaryCards(pregnancyMeta);

  return (
    <div className="rounded-2xl border border-slate-200 bg-card-color-100 p-4 shadow-sm dark:border-slate-700 dark:bg-gray-800">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-dui">
        {t('prenatalExam.section.eyebrow')}
      </p>
      <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
        {t('prenatalExam.section.title')}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
        {helperText}
      </p>
      {summaryCards.length ? (
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((item) => (
            <SummaryCard
              key={`${item.label}-${item.value}`}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface ClinicalPrenatalFormProps {
  initialValues?: Partial<ClinicalPrenatalValues> | null;
  formVersion?: string | number | null;
  pregnancyMeta?: ClinicalPrenatalMeta;
  mode?: 'create' | 'update';
  readOnly?: boolean;
  isSubmitting?: boolean;
  helperText?: string;
  submitLabel?: string;
  showSubmitButton?: boolean;
  showIntroCard?: boolean;
  showStudyDateField?: boolean;
  onDraftChange?: (payload: Partial<ClinicalPrenatalValues>) => void;
  onSubmit: (
    payload: Partial<ClinicalPrenatalValues>
  ) => void | Promise<void>;
}

export function ClinicalPrenatalForm({
  initialValues,
  formVersion,
  pregnancyMeta,
  mode = 'create',
  readOnly = false,
  isSubmitting = false,
  helperText,
  submitLabel,
  showSubmitButton = true,
  showIntroCard = true,
  showStudyDateField = mode === 'create',
  onDraftChange,
  onSubmit,
}: ClinicalPrenatalFormProps) {
  const { t } = useTranslation('Patient');
  const [state, setState] = useState<FormState>(() =>
    hydrate(initialValues, pregnancyMeta?.studyDate, mode)
  );
  const lastSubmittedSnapshotRef = useRef<string>('');
  const queuedPayloadRef = useRef<ClinicalPrenatalValues | null>(null);
  const isSubmittingPayloadRef = useRef(false);
  const serializedState = useMemo(() => serialize(state), [state]);

  useEffect(() => {
    const nextState = hydrate(initialValues, pregnancyMeta?.studyDate, mode);
    setState(nextState);
    lastSubmittedSnapshotRef.current = JSON.stringify(serialize(nextState));
    queuedPayloadRef.current = null;
    isSubmittingPayloadRef.current = false;
  }, [formVersion, initialValues, mode, pregnancyMeta?.studyDate]);

  useEffect(() => {
    onDraftChange?.(serializedState);
  }, [onDraftChange, serializedState]);

  const disabled = readOnly || (mode !== 'update' && isSubmitting);
  const isLiveUpdateMode = mode === 'update' && !readOnly;
  const creatableSelectOptions = useMemo(
    () => ({
      edema: toTranslatedSelectOptions(EDEMA_OPTIONS, t, { includeEmpty: false }),
      skeletal_abnormalities: toTranslatedSelectOptions(
        SKELETAL_ABNORMALITIES_OPTIONS,
        t,
        { includeEmpty: false }
      ),
      conjunctiva_palms_condition: toTranslatedSelectOptions(
        CONJUNCTIVA_PALMS_OPTIONS,
        t,
        { includeEmpty: false }
      ),
      breast_exam: toTranslatedSelectOptions(BREAST_EXAM_OPTIONS, t, {
        includeEmpty: false,
      }),
      rai_if_rh_negative: toTranslatedSelectOptions(RAI_OPTIONS, t, {
        includeEmpty: false,
      }),
      speculum_exam: toTranslatedSelectOptions(SPECULUM_EXAM_OPTIONS, t, {
        includeEmpty: false,
      }),
      cervical_state: toTranslatedSelectOptions(CERVICAL_STATE_OPTIONS, t, {
        includeEmpty: false,
      }),
      pelvic_assessment: toTranslatedSelectOptions(
        PELVIC_ASSESSMENT_OPTIONS,
        t,
        { includeEmpty: false }
      ),
    }),
    [t]
  );
  const otherLabResultOptions = useMemo(
    () =>
      OTHER_LAB_RESULT_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t]
  );
  const treatmentOptions = useMemo(
    () =>
      TREATMENT_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t]
  );
  const drugClassOptions = useMemo(
    () =>
      DRUG_CLASS_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t]
  );
  const otherLabSelections = useMemo(
    () => decodeMultiSelect(state.other_lab_results),
    [state.other_lab_results]
  );
  const treatmentSelections = useMemo(
    () => decodeMultiSelect(state.treatment),
    [state.treatment]
  );
  const drugClassSelections = useMemo(
    () => decodeMultiSelect(state.drug_classes),
    [state.drug_classes]
  );
  const showSkeletalAbnormalitiesNote =
    ['present', 'suspected', 'suspected_dysplasia'].includes(
      state.skeletal_abnormalities
    ) ||
    (hasText(state.skeletal_abnormalities_note) &&
      !isCustomSelectValue('skeletal_abnormalities', state.skeletal_abnormalities));
  const showBreastExamNote =
    hasText(state.breast_exam_note) &&
    !isCustomSelectValue('breast_exam', state.breast_exam);
  const showRaiNote =
    hasText(state.rai_if_rh_negative_note) &&
    !isCustomSelectValue('rai_if_rh_negative', state.rai_if_rh_negative);
  const showOtherLabResultsNote =
    otherLabSelections.includes('other') || hasText(state.other_lab_results_note);
  const showSpeculumExamNote =
    hasText(state.speculum_exam_note) &&
    !isCustomSelectValue('speculum_exam', state.speculum_exam);
  const showTreatmentNote =
    treatmentSelections.includes('other') || hasText(state.treatment_note);
  const showDrugClassesNote =
    drugClassSelections.includes('other') || hasText(state.drug_classes_note);

  const submitPayload = async (payload: ClinicalPrenatalValues) => {
    const nextSnapshot = JSON.stringify(payload);
    if (nextSnapshot === lastSubmittedSnapshotRef.current) {
      return;
    }

    if (isSubmittingPayloadRef.current) {
      queuedPayloadRef.current = payload;
      return;
    }

    const previousSnapshot = lastSubmittedSnapshotRef.current;
    lastSubmittedSnapshotRef.current = nextSnapshot;
    isSubmittingPayloadRef.current = true;

    try {
      await onSubmit(payload);
    } catch (error) {
      lastSubmittedSnapshotRef.current = previousSnapshot;
      throw error;
    } finally {
      isSubmittingPayloadRef.current = false;
      const queuedPayload = queuedPayloadRef.current;
      queuedPayloadRef.current = null;
      if (queuedPayload) {
        void submitPayload(queuedPayload);
      }
    }
  };

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    let nextState: FormState | null = null;
    setState((prev) => {
      nextState = { ...prev, [key]: value };
      return nextState;
    });

    if (isLiveUpdateMode && !readOnly && nextState) {
      void submitPayload(serialize(nextState));
    }
  };


  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submitPayload(serializedState);
  };

  const handleAutoSave = () => {
    if (!isLiveUpdateMode || isSubmitting) {
      return;
    }
    void submitPayload(serializedState);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onBlurCapture={isLiveUpdateMode ? handleAutoSave : undefined}
      className="w-full space-y-4"
    >
      {showIntroCard ? (
        <ClinicalPrenatalIntroCard
          pregnancyMeta={pregnancyMeta}
          helperText={helperText}
        />
      ) : null}

      <fieldset disabled={disabled} className="grid items-start gap-4 xl:grid-cols-2">
        {showStudyDateField ? (
          <div className="xl:col-span-2 flex flex-wrap gap-3">
            <div className="w-full max-w-sm">
              <Field label={t('prenatalExam.fields.recordDate')}>
                <DateInput
                  compact
                  value={state.study_date}
                  onChange={(event) => patch('study_date', event.target.value)}
                />
              </Field>
            </div>
          </div>
        ) : null}

        <SectionCard className="xl:col-span-2">
          <SectionTitle
            title={t('prenatalExam.sections.clinicalExam')}
            subtitle={t('prenatalExam.sections.clinicalExamHint')}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
            <Field label={t('prenatalExam.fields.bloodPressure')}>
              <Input
                value={state.blood_pressure}
                onChange={(event) => patch('blood_pressure', event.target.value)}
                placeholder={t('prenatalExam.placeholders.bloodPressure')}
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.uterineHeight')}
              hint={t('prenatalExam.units.cm')}
            >
              <Input
                type="number"
                step="0.1"
                value={state.uterine_height_cm}
                onChange={(event) =>
                  patch('uterine_height_cm', event.target.value)
                }
                placeholder={t('prenatalExam.placeholders.uterineHeight')}
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.motherHeartRate')}
              hint={t('prenatalExam.units.bpm')}
            >
              <Input
                type="number"
                step="1"
                value={state.mother_heart_rate}
                onChange={(event) =>
                  patch('mother_heart_rate', event.target.value)
                }
              />
            </Field>
            <Field label={t('prenatalExam.fields.edema')}>
              <CreatableSelectField
                value={state.edema}
                onChange={(next) => patch('edema', next)}
                options={creatableSelectOptions.edema}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.skeletalAbnormalities')}>
              <CreatableSelectField
                value={state.skeletal_abnormalities}
                onChange={(next) => patch('skeletal_abnormalities', next)}
                options={creatableSelectOptions.skeletal_abnormalities}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.conjunctivaPalmsCondition')}>
              <CreatableSelectField
                value={state.conjunctiva_palms_condition}
                onChange={(next) => patch('conjunctiva_palms_condition', next)}
                options={creatableSelectOptions.conjunctiva_palms_condition}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.breastExam')}>
              <CreatableSelectField
                value={state.breast_exam}
                onChange={(next) => patch('breast_exam', next)}
                options={creatableSelectOptions.breast_exam}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            {showSkeletalAbnormalitiesNote ? (
              <Field
                label={t('prenatalExam.fields.skeletalAbnormalitiesNote')}
                className="md:col-span-2 2xl:col-span-3"
              >
                <Textarea
                  rows={2}
                  value={state.skeletal_abnormalities_note}
                  onChange={(event) =>
                    patch('skeletal_abnormalities_note', event.target.value)
                  }
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
            {showBreastExamNote ? (
              <Field
                label={t('prenatalExam.fields.breastExamNote')}
                className="md:col-span-2 2xl:col-span-3"
              >
                <Textarea
                  rows={2}
                  value={state.breast_exam_note}
                  onChange={(event) =>
                    patch('breast_exam_note', event.target.value)
                  }
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle
            title={t('prenatalExam.sections.urineDipstick')}
            subtitle={t('prenatalExam.sections.urineDipstickHint')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            <Field label={t('prenatalExam.fields.urineNitrites')}>
              <SelectField
                t={t}
                value={state.urine_nitrites}
                onChange={(next) => patch('urine_nitrites', next)}
                options={DIPSTICK_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.urineLeucocytes')}>
              <SelectField
                t={t}
                value={state.urine_leucocytes}
                onChange={(next) => patch('urine_leucocytes', next)}
                options={DIPSTICK_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.urineAlbuminuria')}>
              <SelectField
                t={t}
                value={state.urine_albuminuria}
                onChange={(next) => patch('urine_albuminuria', next)}
                options={ALBUMINURIA_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.urineBlood')}>
              <SelectField
                t={t}
                value={state.urine_blood}
                onChange={(next) => patch('urine_blood', next)}
                options={DIPSTICK_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.urinePh')}>
              <Input
                value={state.urine_ph}
                onChange={(event) => patch('urine_ph', event.target.value)}
                placeholder={t('prenatalExam.placeholders.urinePh')}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle title={t('prenatalExam.sections.serologies')} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('prenatalExam.fields.rubeola')}>
              <SelectField
                t={t}
                value={state.rubeola}
                onChange={(next) => patch('rubeola', next)}
                options={withCurrentValueOption(RUBEOLA_OPTIONS, state.rubeola, t)}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.toxoplasmosis')}>
              <SelectField
                t={t}
                value={state.toxoplasmosis}
                onChange={(next) => patch('toxoplasmosis', next)}
                options={withCurrentValueOption(
                  TOXOPLASMOSIS_OPTIONS,
                  state.toxoplasmosis,
                  t
                )}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.syphilis')}>
              <SelectField
                t={t}
                value={state.syphilis_tpha_vdrl}
                onChange={(next) => patch('syphilis_tpha_vdrl', next)}
                options={withCurrentValueOption(
                  SYPHILIS_OPTIONS,
                  state.syphilis_tpha_vdrl,
                  t
                )}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.hbsAg')}>
              <SelectField
                t={t}
                value={state.hbs_ag}
                onChange={(next) => patch('hbs_ag', next)}
                options={withCurrentValueOption(HBS_OPTIONS, state.hbs_ag, t)}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.hiv')}>
              <SelectField
                t={t}
                value={state.hiv_serology}
                onChange={(next) => patch('hiv_serology', next)}
                options={withCurrentValueOption(HIV_OPTIONS, state.hiv_serology, t)}
                disabled={disabled}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle
            title={t('prenatalExam.sections.hematology')}
            subtitle={t('prenatalExam.sections.hematologyHint')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label={t('prenatalExam.fields.hemoglobin')}
              hint={t('prenatalExam.units.gPerDl')}
            >
              <Input
                type="number"
                step="0.1"
                value={state.hemoglobin_g_dl}
                onChange={(event) =>
                  patch('hemoglobin_g_dl', event.target.value)
                }
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.hematocrit')}
              hint={t('prenatalExam.units.percent')}
            >
              <Input
                type="number"
                step="0.1"
                value={state.hematocrit_pct}
                onChange={(event) =>
                  patch('hematocrit_pct', event.target.value)
                }
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.platelets')}
              hint={t('prenatalExam.units.gigaPerL')}
            >
              <Input
                type="number"
                step="1"
                value={state.platelets_giga_l}
                onChange={(event) =>
                  patch('platelets_giga_l', event.target.value)
                }
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle
            title={t('prenatalExam.sections.glycemia')}
            subtitle={t('prenatalExam.sections.glycemiaHint')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label={t('prenatalExam.fields.gaj')}
              hint={t('prenatalExam.units.gPerL')}
            >
              <Input
                type="number"
                step="0.01"
                value={state.glycemia_gaj_g_l}
                onChange={(event) =>
                  patch('glycemia_gaj_g_l', event.target.value)
                }
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.hgpoA1h')}
              hint={t('prenatalExam.units.gPerL')}
            >
              <Input
                type="number"
                step="0.01"
                value={state.glycemia_hgpo_a1h_g_l}
                onChange={(event) =>
                  patch('glycemia_hgpo_a1h_g_l', event.target.value)
                }
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.hgpoA2h')}
              hint={t('prenatalExam.units.gPerL')}
            >
              <Input
                type="number"
                step="0.01"
                value={state.glycemia_hgpo_a2h_g_l}
                onChange={(event) =>
                  patch('glycemia_hgpo_a2h_g_l', event.target.value)
                }
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle title={t('prenatalExam.sections.raiAndOther')} />
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={t('prenatalExam.fields.raiIfRhNegative')}
              hint={t('prenatalExam.fields.raiHint')}
            >
              <CreatableSelectField
                value={state.rai_if_rh_negative}
                onChange={(next) => patch('rai_if_rh_negative', next)}
                options={creatableSelectOptions.rai_if_rh_negative}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.otherLabResults')}
              hint={t('prenatalExam.placeholders.multiSelect')}
            >
              <MultiSelectChips
                value={otherLabSelections}
                options={otherLabResultOptions}
                onChange={(next) =>
                  patch('other_lab_results', encodeMultiSelect(next))
                }
                placeholder={t('prenatalExam.placeholders.multiSelect')}
                ariaLabel={t('prenatalExam.fields.otherLabResults')}
              />
            </Field>
            {showRaiNote ? (
              <Field label={t('prenatalExam.fields.raiIfRhNegativeNote')}>
                <Textarea
                  rows={2}
                  value={state.rai_if_rh_negative_note}
                  onChange={(event) =>
                    patch('rai_if_rh_negative_note', event.target.value)
                  }
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
            {showOtherLabResultsNote ? (
              <Field label={t('prenatalExam.fields.otherLabResultsNote')}>
                <Textarea
                  rows={2}
                  value={state.other_lab_results_note}
                  onChange={(event) =>
                    patch('other_lab_results_note', event.target.value)
                  }
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle title={t('prenatalExam.sections.vaginalExam')} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('prenatalExam.fields.speculumExamination')}>
              <CreatableSelectField
                value={state.speculum_exam}
                onChange={(next) => patch('speculum_exam', next)}
                options={creatableSelectOptions.speculum_exam}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.cervicalState')}>
              <CreatableSelectField
                value={state.cervical_state}
                onChange={(next) => patch('cervical_state', next)}
                options={creatableSelectOptions.cervical_state}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.pelvicAssessment')}>
              <CreatableSelectField
                value={state.pelvic_assessment}
                onChange={(next) => patch('pelvic_assessment', next)}
                options={creatableSelectOptions.pelvic_assessment}
                placeholder={t('prenatalExam.placeholders.customOption')}
                disabled={disabled}
              />
            </Field>
            {showSpeculumExamNote ? (
              <Field
                label={t('prenatalExam.fields.speculumExaminationNote')}
                className="sm:col-span-2"
              >
                <Textarea
                  rows={2}
                  value={state.speculum_exam_note}
                  onChange={(event) => patch('speculum_exam_note', event.target.value)}
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard className="xl:col-span-2">
          <SectionTitle
            title={t('prenatalExam.sections.supplementation')}
            subtitle={t('prenatalExam.sections.supplementationHint')}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label={t('prenatalExam.fields.suppIron')}>
              <SelectField
                t={t}
                value={state.supp_iron}
                onChange={(next) =>
                  patch('supp_iron', next as FormState['supp_iron'])
                }
                options={YES_NO_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.suppIronNote')}>
              <Input
                value={state.supp_iron_note}
                onChange={(event) =>
                  patch('supp_iron_note', event.target.value)
                }
                placeholder={t('prenatalExam.placeholders.dosage')}
              />
            </Field>
            <Field label={t('prenatalExam.fields.suppVitaminD')}>
              <SelectField
                t={t}
                value={state.supp_vitamin_d}
                onChange={(next) =>
                  patch('supp_vitamin_d', next as FormState['supp_vitamin_d'])
                }
                options={YES_NO_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.suppVitaminDNote')}>
              <Input
                value={state.supp_vitamin_d_note}
                onChange={(event) =>
                  patch('supp_vitamin_d_note', event.target.value)
                }
                placeholder={t('prenatalExam.placeholders.dosage')}
              />
            </Field>
            <Field label={t('prenatalExam.fields.suppFolicAcid')}>
              <SelectField
                t={t}
                value={state.supp_folic_acid}
                onChange={(next) =>
                  patch('supp_folic_acid', next as FormState['supp_folic_acid'])
                }
                options={YES_NO_OPTIONS}
                disabled={disabled}
              />
            </Field>
            <Field label={t('prenatalExam.fields.suppFolicAcidNote')}>
              <Input
                value={state.supp_folic_acid_note}
                onChange={(event) =>
                  patch('supp_folic_acid_note', event.target.value)
                }
                placeholder={t('prenatalExam.placeholders.dosage')}
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.treatment')}
              className="sm:col-span-2 xl:col-span-4"
              hint={t('prenatalExam.placeholders.multiSelect')}
            >
              <MultiSelectChips
                value={treatmentSelections}
                options={treatmentOptions}
                onChange={(next) => patch('treatment', encodeMultiSelect(next))}
                placeholder={t('prenatalExam.placeholders.multiSelect')}
                ariaLabel={t('prenatalExam.fields.treatment')}
                menuPosition="up"
              />
            </Field>
            <Field
              label={t('prenatalExam.fields.drugClasses')}
              className="sm:col-span-2 xl:col-span-4"
              hint={t('prenatalExam.placeholders.multiSelect')}
            >
              <MultiSelectChips
                value={drugClassSelections}
                options={drugClassOptions}
                onChange={(next) =>
                  patch('drug_classes', encodeMultiSelect(next))
                }
                placeholder={t('prenatalExam.placeholders.multiSelect')}
                ariaLabel={t('prenatalExam.fields.drugClasses')}
                menuPosition="up"
              />
            </Field>
            {showTreatmentNote ? (
              <Field
                label={t('prenatalExam.fields.treatmentNote')}
                className="sm:col-span-2 xl:col-span-4"
              >
                <Textarea
                  rows={2}
                  value={state.treatment_note}
                  onChange={(event) => patch('treatment_note', event.target.value)}
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
            {showDrugClassesNote ? (
              <Field
                label={t('prenatalExam.fields.drugClassesNote')}
                className="sm:col-span-2 xl:col-span-4"
              >
                <Textarea
                  rows={2}
                  value={state.drug_classes_note}
                  onChange={(event) => patch('drug_classes_note', event.target.value)}
                  placeholder={t('prenatalExam.placeholders.precision')}
                />
              </Field>
            ) : null}
            <Field
              label={t('prenatalExam.fields.notes')}
              className="sm:col-span-2 xl:col-span-4"
            >
              <Textarea
                rows={2}
                value={state.notes}
                onChange={(event) => patch('notes', event.target.value)}
              />
            </Field>
          </div>
        </SectionCard>
      </fieldset>

      {!readOnly && mode === 'create' && showSubmitButton ? (
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('prenatalExam.form.saving')
              : mode === 'create'
                ? submitLabel ?? t('prenatalExam.form.create')
                : t('prenatalExam.form.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
